import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { renderSVG } from "uqr";
import { ErrorText } from "../components/ErrorText";
import { Logo } from "../components/Logo";
import type { DataStore, MfaEnrollment, MfaRequirement } from "../data";
import { rememberCode } from "../mfaTrust";

/** Only the digits of a typed code, at most 6 (spaces from copying do not matter). */
const digits = (s: string) => s.replace(/\D/g, "").slice(0, 6);
/** The secret in groups of four, easier to type into the app by hand. */
const grouped = (s: string) => s.replace(/(.{4})/g, "$1 ").trim();

function CodeField({ value, onChange, autoFocus }: { value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
  const { t } = useTranslation();
  return (
    <label className="field wide">
      <span>{t("mfa.code.label")}</span>
      <input
        className="mfa-code-input"
        value={value}
        onChange={(e) => onChange(digits(e.target.value))}
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        aria-label={t("mfa.code.label")}
        autoFocus={autoFocus}
      />
    </label>
  );
}

function Missing({ items }: { items: string[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <p className="missing" role="status">
      {t("required.stillMissing", { fields: items.join(", ") })}
    </p>
  );
}

/** Ten one-time codes, shown once: download or print, then the tick "Elmentettem". */
export function BackupCodes({ codes, email, onDone }: { codes: string[]; email: string; onDone: () => void }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const download = () => {
    const text = [t("mfa.backup.fileTitle", { email }), "", ...codes, "", t("mfa.backup.fileNote"), ""].join("\n");
    try {
      const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "garagevo-tartalek-kodok.txt";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // no download in this browser: printing still works
    }
  };
  return (
    <section className="mfa-backup" aria-labelledby="mfa-backup-title">
      <h2 id="mfa-backup-title">{t("mfa.backup.title")}</h2>
      <p>{t("mfa.backup.intro")}</p>
      <ol className="mfa-codes" aria-label={t("mfa.backup.title")}>
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="mfa-actions">
        <button type="button" onClick={download}>
          {t("mfa.backup.download")}
        </button>
        <button type="button" onClick={() => globalThis.print?.()}>
          {t("mfa.backup.print")}
        </button>
      </div>
      <label className="switch-row mfa-tick">
        <span>{t("mfa.backup.saved")}</span>
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} aria-label={t("mfa.backup.saved")} />
      </label>
      <Missing items={saved ? [] : [t("mfa.backup.saved")]} />
      <button type="button" className="primary" disabled={!saved} onClick={onDone}>
        {t("mfa.backup.done")}
      </button>
    </section>
  );
}

/**
 * Setting up the authenticator: QR code and the key as text, the first code, then the backup codes.
 * `inline` is the Settings form (no full screen card); `onSkip` only where it is not required.
 */
export function MfaSetup({ store, userId, email, reason, inline, onDone, onSkip, onSignOut }: {
  store: DataStore; userId: string; email: string; reason: MfaRequirement["reason"]; inline?: boolean;
  onDone: () => void; onSkip?: () => void; onSignOut?: () => void;
}) {
  const { t } = useTranslation();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    store.mfaEnroll().then((e) => live && setEnrollment(e), (err) => live && setError(err));
    return () => {
      live = false;
    };
  }, [store]);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (!enrollment || code.length !== 6) return;
    setError(null);
    setBusy(true);
    try {
      const list = await store.mfaConfirmEnroll(enrollment.factorId, code);
      rememberCode(userId);
      setCodes(list);
    } catch (err) {
      setError(err);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const body = codes ? (
    <BackupCodes codes={codes} email={email} onDone={onDone} />
  ) : (
    <>
      <h1>{t("mfa.setup.title")}</h1>
      <p>{t(`mfa.setup.why.${reason ?? "optional"}`)}</p>
      <ol className="mfa-steps">
        <li>{t("mfa.setup.step1")}</li>
        <li>
          {t("mfa.setup.step2")}
          {enrollment && (
            <>
              <div className="qr mfa-qr" role="img" aria-label={t("mfa.setup.qr")} dangerouslySetInnerHTML={{ __html: renderSVG(enrollment.uri) }} />
              <a className="button mfa-open" href={enrollment.uri}>
                {t("mfa.setup.openApp")}
              </a>
              <p className="muted">{t("mfa.setup.secretIntro")}</p>
              <p className="mfa-secret" aria-label={t("mfa.setup.secret")}>
                <code>{grouped(enrollment.secret)}</code>
              </p>
            </>
          )}
        </li>
        <li>
          {t("mfa.setup.step3")}
          <form onSubmit={confirm} className="mfa-form">
            <CodeField value={code} onChange={setCode} />
            <ErrorText error={error} />
            <Missing items={code.length === 6 ? [] : [t("mfa.code.label")]} />
            <button type="submit" className="primary" disabled={!enrollment || code.length !== 6 || busy}>
              {t("mfa.setup.confirm")}
            </button>
          </form>
        </li>
      </ol>
      {onSkip && (
        <button type="button" className="link" onClick={onSkip}>
          {t("mfa.setup.later")}
        </button>
      )}
    </>
  );
  if (inline) return <div className="mfa-inline">{body}</div>;
  return (
    <div className="login mfa-screen">
      <div className="login-card">
        <div className="brand">
          <Logo size={40} />
        </div>
        {body}
        {onSignOut && (
          <button type="button" onClick={onSignOut}>
            {t("auth.signOut")}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The code in the middle of the work (step up, see stepUp.ts): Settings, the operator pages, the
 * dialog. `intro` says why.
 */
export function StepUpForm({ store, userId, intro, onDone, onCancel }: {
  store: DataStore; userId: string; intro: string; onDone: () => void; onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setError(null);
    setBusy(true);
    try {
      await store.mfaVerify(code);
      rememberCode(userId);
      onDone();
    } catch (err) {
      setError(err);
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="mfa-form mfa-stepup" onSubmit={submit}>
      <p>{intro}</p>
      <CodeField value={code} onChange={setCode} autoFocus />
      <ErrorText error={error} />
      <Missing items={code.length === 6 ? [] : [t("mfa.code.label")]} />
      <div className="actions">
        <button type="submit" className="primary" disabled={code.length !== 6 || busy}>
          {t("mfa.stepUp.submit")}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            {t("mfa.stepUp.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * Every day: the 6 digit code after the password, or a backup code when the phone is lost. With the
 * tick the server remembers this device for 30 days. The operator may tick it too (for his own
 * workshop); a note says that the operator pages still ask the code.
 */
export function MfaCode({ store, userId, operator = false, onDone, onRecovered, onSignOut }: {
  store: DataStore; userId: string; operator?: boolean; onDone: () => void; onRecovered: () => void; onSignOut: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"code" | "backup">("code");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const backupOk = backup.replace(/[^0-9A-Za-z]/g, "").length >= 10;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === "code") {
        await store.mfaVerify(code);
        rememberCode(userId);
        if (trust) {
          // not remembered (e.g. storage blocked, network): the code is simply asked next time
          await store.mfaTrustDevice().catch(() => undefined);
        }
        onDone();
      } else {
        // the store signs out: the parent shows the sign in screen with a note
        await store.mfaRecover(backup);
        onRecovered();
      }
    } catch (err) {
      setError(err);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login mfa-screen">
      <div className="login-card">
        <div className="brand">
          <Logo size={40} />
        </div>
        {mode === "code" ? (
          <form onSubmit={submit}>
            <h1>{t("mfa.code.title")}</h1>
            <p>{t("mfa.code.intro")}</p>
            <CodeField value={code} onChange={setCode} autoFocus />
            <label className="switch-row mfa-tick">
              <span>
                {t("mfa.code.trust")}
                <small className="muted">{t("mfa.code.trustHint")}</small>
                {operator && <small className="muted mfa-operator-note">{t("mfa.code.trustOperator")}</small>}
              </span>
              <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} aria-label={t("mfa.code.trust")} />
            </label>
            <ErrorText error={error} />
            <Missing items={code.length === 6 ? [] : [t("mfa.code.label")]} />
            <button type="submit" className="primary" disabled={code.length !== 6 || busy}>
              {t("mfa.code.submit")}
            </button>
            <button type="button" className="link" onClick={() => { setError(null); setMode("backup"); }}>
              {t("mfa.code.useBackup")}
            </button>
          </form>
        ) : (
          <form onSubmit={submit}>
            <h1>{t("mfa.backupLogin.title")}</h1>
            <p>{t("mfa.backupLogin.intro")}</p>
            <label className="field wide">
              <span>{t("mfa.backupLogin.label")}</span>
              <input value={backup} onChange={(e) => setBackup(e.target.value.slice(0, 20))} autoComplete="off" autoCapitalize="characters" aria-label={t("mfa.backupLogin.label")} />
            </label>
            <ErrorText error={error} />
            <Missing items={backupOk ? [] : [t("mfa.backupLogin.label")]} />
            <button type="submit" className="primary" disabled={!backupOk || busy}>
              {t("mfa.backupLogin.submit")}
            </button>
            <p className="muted small">{t("mfa.noBackup")}</p>
            <button type="button" className="link" onClick={() => { setError(null); setMode("code"); }}>
              {t("mfa.backupLogin.back")}
            </button>
          </form>
        )}
        <button type="button" onClick={onSignOut}>
          {t("auth.signOut")}
        </button>
      </div>
    </div>
  );
}
